import React, { useState, useEffect, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { X, MapPin, Phone, CheckCircle2, AlertCircle, Search, HelpCircle } from 'lucide-react';

/**
 * 치과 매칭 후보 선택 모달
 *
 * 진입 흐름:
 *   1. 가입 직후 Register.js가 findHospitalCandidates 호출 → sessionStorage 저장
 *   2. 첫 로그인 시 sessionStorage.hospitalMatchPending === 'true' 검사
 *   3. 본 모달 노출 → 사용자가 1개 선택 → claimHospital 호출 → sessionStorage 클리어
 *   4. 후보 0개 또는 "목록에 없음" → requestNewHospital → 관리자 큐
 *
 * 안전 정책:
 *   - 자동 매칭 X (사용자 명시적 선택)
 *   - 90%+ 만 1순위 자동 선택 (확인만 하면 됨)
 *   - 70~90% 수동 선택 강제
 */
function HospitalCandidatePicker({ candidates: initialCandidates, onClose, onClaimed, businessName }) {
    const candidates = initialCandidates || [];
    const [selectedId, setSelectedId] = useState(() => {
        // 1순위 점수 90+이면 자동 선택
        if (candidates.length > 0 && (candidates[0]._score >= 90)) {
            return candidates[0].id;
        }
        return null;
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [showRequestForm, setShowRequestForm] = useState(false);
    const [requestForm, setRequestForm] = useState({ businessName: businessName || '', address: '', phone: '' });

    const handleClaim = async () => {
        if (!selectedId) {
            setError('후보를 선택해주세요');
            return;
        }
        const cand = candidates.find(c => c.id === selectedId);
        setSubmitting(true);
        setError(null);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const claim = httpsCallable(fns, 'claimHospital');
            await claim({ hospitalId: selectedId, matchScore: cand?._score || 0 });
            // 성공 → sessionStorage 클리어
            sessionStorage.removeItem('hospitalMatchCandidates');
            sessionStorage.removeItem('hospitalMatchPending');
            onClaimed?.({ hospitalId: selectedId, hospitalName: cand?.name });
            onClose?.();
        } catch (err) {
            setError(err.message || '치과 등록 실패');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequestNew = async () => {
        if (!requestForm.businessName.trim()) {
            setError('업체명을 입력해주세요');
            return;
        }
        setSubmitting(true);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const req = httpsCallable(fns, 'requestNewHospital');
            await req(requestForm);
            sessionStorage.removeItem('hospitalMatchCandidates');
            sessionStorage.removeItem('hospitalMatchPending');
            alert('신규 치과 등록 요청이 접수되었습니다. 관리자 검토 후 연락드릴게요.');
            onClose?.();
        } catch (err) {
            setError(err.message || '요청 실패');
        } finally {
            setSubmitting(false);
        }
    };

    const handleLater = () => {
        // 나중에 등록 — 다음 로그인 시 다시 노출됨
        sessionStorage.removeItem('hospitalMatchPending');
        onClose?.();
    };

    return (
        <div style={S.overlay}>
            <div style={S.modal}>
                <div style={S.header}>
                    <div>
                        <h2 style={S.title}>우리 치과를 선택해주세요</h2>
                        <p style={S.subtitle}>
                            {businessName ? `"${businessName}" ` : ''}HIRA 보건복지부 데이터에서 검색한 결과입니다
                        </p>
                    </div>
                    <button onClick={handleLater} style={S.closeBtn} aria-label="닫기"><X size={20} /></button>
                </div>

                {!showRequestForm ? (
                    <>
                        {candidates.length === 0 ? (
                            <div style={S.empty}>
                                <Search size={32} color="#94a3b8" />
                                <p style={{ marginTop: 12, fontSize: 14, color: '#64748b' }}>
                                    매칭되는 치과를 찾지 못했습니다
                                </p>
                                <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                                    HIRA에 미등록된 신규 치과일 수 있습니다.<br />
                                    아래에서 직접 등록을 요청해주세요.
                                </p>
                                <button
                                    onClick={() => setShowRequestForm(true)}
                                    style={S.primaryBtn}
                                >
                                    신규 치과 등록 요청 →
                                </button>
                            </div>
                        ) : (
                            <>
                                <div style={S.candidateList}>
                                    {candidates.map((c, i) => {
                                        const sel = selectedId === c.id;
                                        const accuracy = Math.min(100, c._score || 0);
                                        return (
                                            <label
                                                key={c.id}
                                                style={{ ...S.candidate, ...(sel ? S.candidateActive : {}) }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="hospital"
                                                    value={c.id}
                                                    checked={sel}
                                                    onChange={() => setSelectedId(c.id)}
                                                    style={{ marginTop: 4 }}
                                                />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <strong style={{ fontSize: 14, color: '#0f172a' }}>{c.name}</strong>
                                                        {i === 0 && accuracy >= 90 && (
                                                            <span style={S.recommendedBadge}>추천</span>
                                                        )}
                                                        <span style={{
                                                            ...S.accuracyBadge,
                                                            background: accuracy >= 90 ? '#10b981'
                                                                : accuracy >= 70 ? '#f59e0b'
                                                                : '#ef4444',
                                                        }}>
                                                            {accuracy >= 90 ? '🟢' : accuracy >= 70 ? '🟡' : '🟠'} 정확도 {accuracy}%
                                                        </span>
                                                    </div>
                                                    <div style={S.candidateMeta}>
                                                        <MapPin size={11} /> {c.address || '주소 없음'}
                                                    </div>
                                                    {c.phone && (
                                                        <div style={S.candidateMeta}>
                                                            <Phone size={11} /> {c.phone}
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>

                                {/* 점수 안내 */}
                                <div style={S.scoreLegend}>
                                    <span>🟢 90%+ 자동 선택</span>
                                    <span>🟡 70~90% 확인 후 선택</span>
                                    <span>🟠 50~70% 신중히 확인</span>
                                </div>

                                {/* 점수 70 미만 강제 확인 */}
                                {selectedId && (() => {
                                    const c = candidates.find(x => x.id === selectedId);
                                    if (c && (c._score || 0) < 70) {
                                        return (
                                            <div style={S.warning}>
                                                <AlertCircle size={14} color="#92400e" />
                                                <span>정확도가 낮습니다. 본인 치과가 맞는지 다시 확인해주세요.</span>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}

                                <button
                                    onClick={() => setShowRequestForm(true)}
                                    style={S.linkBtn}
                                >
                                    <HelpCircle size={12} /> 위 목록에 우리 치과가 없어요 →
                                </button>
                            </>
                        )}
                    </>
                ) : (
                    /* 신규 치과 등록 요청 폼 */
                    <div style={{ padding: '4px 0' }}>
                        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.6 }}>
                            관리자가 사업자등록증 확인 후 등록 처리합니다 (1~3일).<br />
                            등록 완료 시 가입한 이메일로 안내드립니다.
                        </p>
                        <div style={S.field}>
                            <label style={S.label}>치과명 *</label>
                            <input
                                type="text"
                                value={requestForm.businessName}
                                onChange={(e) => setRequestForm({ ...requestForm, businessName: e.target.value })}
                                placeholder="예: 김포연세사랑치과의원"
                                style={S.input}
                            />
                        </div>
                        <div style={S.field}>
                            <label style={S.label}>주소</label>
                            <input
                                type="text"
                                value={requestForm.address}
                                onChange={(e) => setRequestForm({ ...requestForm, address: e.target.value })}
                                placeholder="도로명/지번 주소"
                                style={S.input}
                            />
                        </div>
                        <div style={S.field}>
                            <label style={S.label}>전화번호</label>
                            <input
                                type="text"
                                value={requestForm.phone}
                                onChange={(e) => setRequestForm({ ...requestForm, phone: e.target.value })}
                                placeholder="031-XXX-XXXX"
                                style={S.input}
                            />
                        </div>
                        <button onClick={() => setShowRequestForm(false)} style={S.linkBtn}>
                            ← 후보 목록으로 돌아가기
                        </button>
                    </div>
                )}

                {error && (
                    <div style={S.error}><AlertCircle size={14} /> {error}</div>
                )}

                <div style={S.footer}>
                    <button onClick={handleLater} style={S.cancelBtn}>나중에</button>
                    {!showRequestForm ? (
                        <button
                            onClick={handleClaim}
                            disabled={!selectedId || submitting}
                            style={{ ...S.primaryBtn, opacity: (!selectedId || submitting) ? 0.5 : 1 }}
                        >
                            {submitting ? '등록 중...' : '선택 완료'}
                        </button>
                    ) : (
                        <button
                            onClick={handleRequestNew}
                            disabled={submitting || !requestForm.businessName.trim()}
                            style={{ ...S.primaryBtn, opacity: submitting ? 0.5 : 1 }}
                        >
                            {submitting ? '요청 중...' : '등록 요청'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

const S = {
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    },
    modal: {
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
        maxHeight: '92vh', overflowY: 'auto', padding: 24,
    },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: 16,
    },
    title: { margin: 0, fontSize: 18, fontWeight: 900, color: '#0f172a' },
    subtitle: { margin: '4px 0 0', fontSize: 12, color: '#64748b' },
    closeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 },
    candidateList: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 },
    candidate: {
        display: 'flex', gap: 10, alignItems: 'flex-start',
        padding: 12, border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer',
    },
    candidateActive: { borderColor: '#0ea5e9', background: '#f0f9ff' },
    candidateMeta: {
        marginTop: 4, fontSize: 11, color: '#64748b',
        display: 'flex', alignItems: 'center', gap: 4,
    },
    recommendedBadge: {
        padding: '2px 6px', background: '#10b981', color: '#fff',
        borderRadius: 3, fontSize: 9, fontWeight: 800,
    },
    accuracyBadge: {
        padding: '2px 6px', borderRadius: 3, color: '#fff',
        fontSize: 9, fontWeight: 800,
    },
    scoreLegend: {
        display: 'flex', gap: 12, padding: 10, background: '#f8fafc',
        borderRadius: 8, fontSize: 10, color: '#64748b', justifyContent: 'space-around',
        flexWrap: 'wrap', marginBottom: 8,
    },
    warning: {
        padding: 10, background: '#fef3c7', borderRadius: 8,
        fontSize: 11, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6,
        marginBottom: 8,
    },
    linkBtn: {
        background: 'transparent', border: 'none', color: '#0ea5e9',
        fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '6px 0',
        display: 'inline-flex', alignItems: 'center', gap: 4,
    },
    empty: { padding: '40px 20px', textAlign: 'center' },
    field: { marginBottom: 12 },
    label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 4 },
    input: {
        width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1',
        borderRadius: 8, fontSize: 13, boxSizing: 'border-box',
    },
    error: {
        padding: 10, background: '#fee2e2', color: '#991b1b',
        borderRadius: 8, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
        marginTop: 12,
    },
    footer: {
        display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16,
    },
    cancelBtn: {
        padding: '10px 16px', background: 'transparent', color: '#64748b',
        border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
    },
    primaryBtn: {
        padding: '10px 18px', background: '#0ea5e9', color: '#fff',
        border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: 'pointer',
    },
};

export default HospitalCandidatePicker;
